// PurchasesPage.jsx - Version finale corrigée
import React, { useEffect, useState, useMemo } from "react";
import salesStore from "../store/salesStore";
import {
  BiCalendar,
  BiReset,
  BiSearch,
  BiPurchaseTag,
  BiUser,
  BiListUl,
  BiX,
  BiFilter,
  BiChevronLeft,
  BiChevronRight,
  BiChevronsLeft,
  BiChevronsRight,
  BiCheckCircle,
  BiTime,
  BiErrorCircle,
  BiCreditCard,
  BiMoney,
  BiMobileAlt,
} from "react-icons/bi";
import { FiDownload, FiPrinter } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";

export default function PurchasesPage() {
  const {
    allPurchasesUser,
    isLoadingPurchases,
    getUserPurchasesWithDateRange,
  } = salesStore();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);

  // États pour les filtres de période
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [periodFilter, setPeriodFilter] = useState("all");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Chargement initial
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      await getUserPurchasesWithDateRange();
    } catch (error) {
      console.error("Erreur lors du chargement initial:", error);
    }
  };

  // Fonction pour obtenir les dates d'une période
  const getPeriodDates = (period) => {
    const today = new Date();
    let start = new Date(today);
    let end = new Date(today);

    const formatDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    };

    switch (period) {
      case "today":
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case "week":
        start.setDate(today.getDate() - today.getDay());
        start.setHours(0, 0, 0, 0);
        end.setDate(today.getDate() + (6 - today.getDay()));
        end.setHours(23, 59, 59, 999);
        break;
      case "month":
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
        break;
      default:
        return { startDate: "", endDate: "" };
    }

    return {
      startDate: formatDate(start),
      endDate: formatDate(end),
    };
  };

  // Charger les achats avec les filtres
  const loadPurchases = async (
    period = periodFilter,
    customStartDate = startDate,
    customEndDate = endDate,
  ) => {
    try {
      let start = customStartDate;
      let end = customEndDate;

      if (period !== "custom" && period !== "all") {
        const dates = getPeriodDates(period);
        start = dates.startDate;
        end = dates.endDate;
        setStartDate(dates.startDate);
        setEndDate(dates.endDate);
      }

      if (period === "all" || !start || !end) {
        await getUserPurchasesWithDateRange();
      } else {
        await getUserPurchasesWithDateRange(start, end);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des achats:", error);
    } finally {
      setCurrentPage(1);
    }
  };

  const handlePeriodChange = async (period) => {
    setPeriodFilter(period);

    if (period === "all") {
      setStartDate("");
      setEndDate("");
      await loadPurchases("all", "", "");
    } else if (period !== "custom") {
      const dates = getPeriodDates(period);
      await loadPurchases(period, dates.startDate, dates.endDate);
    }
  };

  // Effet pour les dates personnalisées
  useEffect(() => {
    if (periodFilter === "custom" && startDate && endDate) {
      const timer = setTimeout(() => {
        loadPurchases("custom", startDate, endDate);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [startDate, endDate, periodFilter]);

  // Fonction pour ouvrir le modal des services
  const handleShowServices = (purchase) => {
    setSelectedPurchase(purchase);
    setShowServicesModal(true);
  };

  // Fonction pour fermer le modal
  const handleCloseModal = () => {
    setShowServicesModal(false);
    setSelectedPurchase(null);
  };

  // Obtenir les services d'un achat
  const getPurchaseServices = (purchase) => {
    return purchase.services || [];
  };

  // Calculer le total des services
  const calculateServicesTotal = (services) => {
    return services.reduce((total, service) => {
      const price = service.unitPrice || service.price || 0;
      const quantity = service.quantity || 1;
      return total + parseFloat(price) * parseInt(quantity);
    }, 0);
  };

  const resetFilters = async () => {
    setPeriodFilter("all");
    setStartDate("");
    setEndDate("");
    setSearchTerm("");
    setStatusFilter("");
    setCurrentPage(1);
    await loadPurchases("all", "", "");
  };

  const getPeriodLabel = () => {
    switch (periodFilter) {
      case "today":
        return "Aujourd'hui";
      case "week":
        return "Cette semaine";
      case "month":
        return "Ce mois";
      case "custom":
        return "Période personnalisée";
      default:
        return "Toutes les périodes";
    }
  };

  // Filtrage des achats
  const filteredPurchases = useMemo(() => {
    return allPurchasesUser.filter((purchase) => {
      const matchesSearch =
        !searchTerm ||
        purchase.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (purchase.extraData &&
          `${purchase.extraData.name || "-"}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase())) ||
        purchase.method?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = !statusFilter || purchase.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [allPurchasesUser, searchTerm, statusFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredPurchases.length / itemsPerPage);
  const paginatedPurchases = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredPurchases.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredPurchases, currentPage, itemsPerPage]);

  // Calcul des statistiques
  const stats = useMemo(() => {
    const totalPurchases = allPurchasesUser.length;
    const directPurchases = allPurchasesUser.filter((p) => p.is_vente).length;
    const online = allPurchasesUser.filter((p) => !p.is_vente).length;
    const subscriptions = allPurchasesUser.filter((p) => p.subscription).length;
    const totalAmount = allPurchasesUser
      .filter((p) => p.status === "COMPLETED")
      .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

    return {
      totalPurchases,
      directPurchases,
      online,
      subscriptions,
      totalAmount,
    };
  }, [allPurchasesUser]);

  // Fonction pour formater le montant
  const formatAmount = (amount) => {
    return parseFloat(amount).toLocaleString("fr-FR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  // Fonction pour obtenir la couleur du statut
  const getStatusColor = (status) => {
    switch (status) {
      case "COMPLETED":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "PENDING":
        return "bg-amber-50 text-amber-700 border-amber-200";
      case "FAILED":
        return "bg-rose-50 text-rose-700 border-rose-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  // Fonction pour obtenir le libellé du statut
  const getStatusLabel = (status) => {
    switch (status) {
      case "COMPLETED":
        return "Complété";
      case "PENDING":
        return "En attente";
      case "FAILED":
        return "Échoué";
      default:
        return status;
    }
  };

  // Fonction pour obtenir l'icône du statut
  const getStatusIcon = (status) => {
    switch (status) {
      case "COMPLETED":
        return <BiCheckCircle className="w-4 h-4" />;
      case "PENDING":
        return <BiTime className="w-4 h-4" />;
      case "FAILED":
        return <BiErrorCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  // Fonction pour obtenir la couleur du type
  const getTypeColor = (purchase) => {
    if (purchase.appointment) return "bg-blue-50 text-blue-700 border-blue-200";
    if (purchase.subscription)
      return "bg-violet-50 text-violet-700 border-violet-200";
    if (purchase.is_vente)
      return "bg-indigo-50 text-indigo-700 border-indigo-200";
    return "bg-gray-50 text-gray-700 border-gray-200";
  };

  // Fonction pour obtenir le libellé du type
  const getTypeLabel = (purchase) => {
    if (purchase.appointment) return "Réservation";
    if (purchase.subscription) return "Abonnement";
    if (purchase.is_vente) return "Achat direct";
    return "Transaction";
  };

  // Fonction pour obtenir la couleur de la méthode de paiement
  const getMethodColor = (method) => {
    switch (method) {
      case "CASH":
        return "bg-green-50 text-green-700 border-green-200";
      case "CARD":
        return "bg-secondary-50 text-secondary-700 border-secondary-200";
      case "MOBILE_MONEY":
        return "bg-purple-50 text-purple-700 border-purple-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  // Fonction pour obtenir l'icône de la méthode de paiement
  const getMethodIcon = (method) => {
    switch (method) {
      case "CASH":
        return <BiMoney className="w-3.5 h-3.5" />;
      case "CARD":
        return <BiCreditCard className="w-3.5 h-3.5" />;
      case "MOBILE_MONEY":
        return <BiMobileAlt className="w-3.5 h-3.5" />;
      default:
        return null;
    }
  };

  // Format de date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Format de date avec heure
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Fonction pour rendre les cartes sur mobile
  const renderPurchaseCard = (purchase) => (
    <motion.div
      key={purchase.id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-gray-200 mb-4 last:mb-0"
    >
      <div className="p-4 border-b border-gray-100 bg-gray-50">
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span
                className={`inline-flex items-center gap-1 px-2 py-1 border ${getStatusColor(purchase.status)} font-nunito font-semibold text-xs`}
              >
                {getStatusIcon(purchase.status)}
                {getStatusLabel(purchase.status)}
              </span>
              <span
                className={`inline-flex items-center gap-1 px-2 py-1 border ${getTypeColor(purchase)} font-nunito font-semibold text-xs`}
              >
                {getTypeLabel(purchase)}
              </span>
            </div>
            <p className="font-nunito text-xs text-gray-500">
              {formatDate(purchase.created_at)}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4">
        {/* Montant et méthode de paiement */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <p className="font-poppins font-bold text-xl text-gray-900">
              {formatAmount(purchase.amount)} FCFA
            </p>
          </div>
          <span
            className={`inline-flex items-center gap-1 px-3 py-1 border ${getMethodColor(purchase.method)} font-nunito font-semibold text-xs`}
          >
            {getMethodIcon(purchase.method)}
            {purchase.method}
          </span>
        </div>

        {/* Informations de paiement (extraData) */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-100">
          <div className="flex items-center gap-2 mb-2">
            <BiUser className="w-4 h-4 text-blue-600" />
            <span className="font-nunito font-semibold text-xs text-blue-700">
              Informations de paiement
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 font-nunito text-xs text-gray-700">
            <div>
              <span className="text-gray-500">Nom:</span>{" "}
              {purchase.extraData?.name ||
                `${purchase.user.nom} ${purchase.user.prenom}`}
            </div>
            <div className="col-span-2">
              <span className="text-gray-500">Email:</span>{" "}
              {purchase.extraData?.email || purchase.user.email}
            </div>
            <div className="col-span-2">
              <span className="text-gray-500">Téléphone:</span>{" "}
              {purchase.extraData?.telephone || purchase.user.telephone}
            </div>
          </div>
        </div>

        {/* Services et bouton d'action */}
        <div className="flex justify-between items-center pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <BiListUl className="w-4 h-4 text-gray-400" />
            <span className="font-nunito text-sm text-gray-700">
              {purchase.services?.length || 0} service
              {purchase.services?.length !== 1 ? "s" : ""}
            </span>
          </div>
          <button
            onClick={() => handleShowServices(purchase)}
            className="px-3 py-1.5 bg-primary text-white hover:bg-primary/90 font-nunito font-semibold text-xs transition-colors"
          >
            Détails
          </button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary to-secondary-100 py-6 px-4">
      <div className="w-full bg-white max-w-6xl p-4 sm:p-6 md:p-8 mx-auto">
        {/* En-tête avec titre et actions */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="font-poppins font-bold text-2xl text-gray-900">
                Mes Achats
              </h1>
              <p className="font-nunito text-sm text-gray-600 mt-1">
                Historique complet de tous mes achats et réservations
              </p>
            </div>
            {/* <div className="flex gap-2">
              <button className="hidden sm:flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 font-nunito font-semibold text-sm hover:bg-gray-50 transition-colors">
                <FiPrinter className="w-4 h-4" />
                <span className="hidden sm:inline">Imprimer</span>
              </button>
              <button className="hidden sm:flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 font-nunito font-semibold text-sm hover:bg-gray-50 transition-colors">
                <FiDownload className="w-4 h-4" />
                <span className="hidden sm:inline">Exporter</span>
              </button>
            </div> */}
          </div>

          {/* Cartes de statistiques */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-nunito text-xs text-gray-500">
                    Total Achats
                  </p>
                  <p className="font-poppins font-bold text-xl text-gray-900 mt-1">
                    {stats.totalPurchases}
                  </p>
                </div>
                <div className="p-3 bg-primary/10">
                  <BiPurchaseTag className="w-5 h-5 text-primary" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-nunito text-xs text-gray-500">
                    Montant Total
                  </p>
                  <p className="font-poppins font-bold text-xl text-gray-900 mt-1">
                    {formatAmount(stats.totalAmount)} FCFA
                  </p>
                </div>
                <div className="p-3 bg-emerald-100">
                  <BiPurchaseTag className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-nunito text-xs text-gray-500">
                    Paiement en ligne
                  </p>
                  <p className="font-poppins font-bold text-xl text-gray-900 mt-1">
                    {stats.online}
                  </p>
                </div>
                <div className="p-3 bg-blue-100">
                  <BiCalendar className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-nunito text-xs text-gray-500">
                    Paiement physique
                  </p>
                  <p className="font-poppins font-bold text-xl text-gray-900 mt-1">
                    {stats.directPurchases}
                  </p>
                </div>
                <div className="p-3 bg-indigo-100">
                  <BiPurchaseTag className="w-5 h-5 text-indigo-600" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section de filtres */}
        <div className="bg-white border border-gray-200 mb-6">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <BiFilter className="w-5 h-5 text-gray-400" />
              <h2 className="font-poppins font-semibold text-lg text-gray-900">
                Filtres et Recherche
              </h2>
            </div>

            {/* Barre de recherche */}
            <div className="mb-4">
              <div className="relative">
                <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Rechercher par méthode de paiement, statut ou type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-nunito text-sm"
                />
              </div>
            </div>

            {/* Filtres avancés */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Filtre période */}
              {/* <div>
                <label className="block font-nunito font-semibold text-xs text-gray-700 mb-2">
                  Période
                </label>
                <select
                  value={periodFilter}
                  onChange={(e) => handlePeriodChange(e.target.value)}
                  className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-nunito text-sm"
                >
                  <option value="all">Toutes les périodes</option>
                  <option value="today">Aujourd'hui</option>
                  <option value="week">Cette semaine</option>
                  <option value="month">Ce mois</option>
                  <option value="custom">Période personnalisée</option>
                </select>
              </div> */}

              {/* Dates personnalisées */}
              {/* {periodFilter === "custom" && (
                <>
                  <div>
                    <label className="block font-nunito font-semibold text-xs text-gray-700 mb-2">
                      Date de début
                    </label>
                    <div className="flex items-center gap-2 border border-gray-300 px-3 py-2">
                      <BiCalendar className="text-gray-400 w-5 h-5" />
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full focus:outline-none font-nunito text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block font-nunito font-semibold text-xs text-gray-700 mb-2">
                      Date de fin
                    </label>
                    <div className="flex items-center gap-2 border border-gray-300 px-3 py-2">
                      <BiCalendar className="text-gray-400 w-5 h-5" />
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full focus:outline-none font-nunito text-sm"
                      />
                    </div>
                  </div>
                </>
              )} */}

              {/* Filtre statut */}
              <div>
                <label className="block font-nunito font-semibold text-xs text-gray-700 mb-2">
                  Statut
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-nunito text-sm"
                >
                  <option value="">Tous les statuts</option>
                  <option value="COMPLETED">Complété</option>
                  <option value="PENDING">En attente</option>
                  <option value="FAILED">Échoué</option>
                </select>
              </div>
            </div>

            {/* Résumé des filtres actifs */}
            {(periodFilter !== "all" || searchTerm || statusFilter) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-nunito text-xs text-gray-600">
                    Filtres actifs :
                  </span>
                  {periodFilter !== "all" && (
                    <span className="inline-flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 font-nunito font-semibold text-xs">
                      {getPeriodLabel()}
                    </span>
                  )}
                  {searchTerm && (
                    <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-1 font-nunito font-semibold text-xs">
                      Recherche : "{searchTerm}"
                    </span>
                  )}
                  {statusFilter && (
                    <span className="inline-flex items-center gap-1 bg-violet-100 text-violet-700 px-2 py-1 font-nunito font-semibold text-xs">
                      Statut : {getStatusLabel(statusFilter)}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Actions de filtre */}
          <div className="p-4 bg-gray-50 flex justify-between items-center">
            <div className="font-nunito text-sm text-gray-600">
              <span className="font-semibold">{filteredPurchases.length}</span>{" "}
              achat{filteredPurchases.length !== 1 ? "s" : ""} trouvé
              {filteredPurchases.length !== 1 ? "s" : ""}
            </div>
            <div className="flex gap-2">
              {(periodFilter !== "all" || searchTerm || statusFilter) && (
                <button
                  onClick={resetFilters}
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 font-nunito font-semibold text-sm transition-colors"
                >
                  <BiReset className="w-4 h-4" />
                  <span className="hidden sm:inline">Réinitialiser</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Liste des achats - Version responsive */}
        <div className="bg-white border border-gray-200 mb-6 overflow-hidden">
          {/* En-tête du tableau/des cartes */}
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-poppins font-semibold text-lg text-gray-900">
              Liste des Achats
              {periodFilter !== "all" && ` - ${getPeriodLabel()}`}
            </h2>
          </div>

          {isLoadingPurchases ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
              <p className="font-nunito text-sm text-gray-600 mt-2">
                Chargement des achats...
              </p>
            </div>
          ) : paginatedPurchases.length === 0 ? (
            <div className="p-8 text-center">
              <BiPurchaseTag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="font-nunito text-base text-gray-600">
                Aucun achat trouvé
              </p>
              <p className="font-nunito text-sm text-gray-500 mt-1">
                Essayez de modifier vos filtres de recherche
              </p>
            </div>
          ) : (
            <>
              {/* Version desktop - Tableau */}
              {/* Pagination */}
              <div className="p-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="font-nunito text-sm text-gray-600">
                      Afficher
                    </span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="border border-gray-300 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-nunito text-sm"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="font-nunito text-sm text-gray-600">
                      éléments
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <BiChevronsLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <BiChevronLeft className="w-4 h-4" />
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`w-8 h-8 flex items-center justify-center font-nunito text-sm ${
                            currentPage === pageNum
                              ? "bg-primary text-white border border-primary"
                              : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <BiChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <BiChevronsRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="font-nunito text-sm text-gray-600">
                  Page {currentPage} sur {totalPages} •{" "}
                  {filteredPurchases.length} résultat
                  {filteredPurchases.length !== 1 ? "s" : ""}
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left p-4 font-poppins font-semibold text-sm text-gray-900">
                          Statut
                        </th>
                        <th className="text-left p-4 font-poppins font-semibold text-sm text-gray-900">
                          Type
                        </th>
                        <th className="text-left p-4 font-poppins font-semibold text-sm text-gray-900">
                          Montant
                        </th>
                        <th className="text-left p-4 font-poppins font-semibold text-sm text-gray-900">
                          Méthode
                        </th>
                        <th className="text-left p-4 font-poppins font-semibold text-sm text-gray-900">
                          Date
                        </th>
                        <th className="text-left p-4 font-poppins font-semibold text-sm text-gray-900">
                          Services
                        </th>
                        <th className="text-left p-4 font-poppins font-semibold text-sm text-gray-900">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPurchases.map((purchase) => (
                        <motion.tr
                          key={purchase.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          <td className="p-4">
                            <span
                              className={`inline-flex items-center gap-1 px-3 py-1 border ${getStatusColor(purchase.status)} font-nunito font-semibold text-xs`}
                            >
                              {getStatusIcon(purchase.status)}
                              {getStatusLabel(purchase.status)}
                            </span>
                          </td>
                          <td className="p-4">
                            <span
                              className={`inline-flex items-center gap-1 px-3 py-1 border ${getTypeColor(purchase)} font-nunito font-semibold text-xs`}
                            >
                              {getTypeLabel(purchase)}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="font-poppins font-bold text-base text-gray-900">
                              {formatAmount(purchase.amount)} FCFA
                            </div>
                          </td>
                          <td className="p-4">
                            <span
                              className={`inline-flex items-center gap-1 px-3 py-1 border ${getMethodColor(purchase.method)} font-nunito font-semibold text-xs`}
                            >
                              {getMethodIcon(purchase.method)}
                              {purchase.method}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="font-nunito text-sm text-gray-700">
                              {formatDate(purchase.created_at)}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <span className="font-nunito text-sm text-gray-700">
                                {purchase.services?.length || 0} service
                                {(purchase.services?.length || 0) !== 1
                                  ? "s"
                                  : ""}
                              </span>
                              {(purchase.services?.length || 0) > 0 && (
                                <button
                                  onClick={() => handleShowServices(purchase)}
                                  className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary hover:bg-primary/20 font-nunito font-semibold text-xs transition-colors"
                                >
                                  <BiListUl className="w-3 h-3" />
                                  Voir
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <button
                              onClick={() => handleShowServices(purchase)}
                              className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 font-nunito font-semibold text-xs transition-colors"
                            >
                              Détails
                            </button>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Version mobile/tablette - Cartes */}
              <div className="lg:hidden">
                <div className="p-4">
                  {paginatedPurchases.map(renderPurchaseCard)}
                </div>
              </div>

              {/* Pagination */}
              <div className="p-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="font-nunito text-sm text-gray-600">
                      Afficher
                    </span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="border border-gray-300 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-nunito text-sm"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="font-nunito text-sm text-gray-600">
                      éléments
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <BiChevronsLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <BiChevronLeft className="w-4 h-4" />
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`w-8 h-8 flex items-center justify-center font-nunito text-sm ${
                            currentPage === pageNum
                              ? "bg-primary text-white border border-primary"
                              : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <BiChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <BiChevronsRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="font-nunito text-sm text-gray-600">
                  Page {currentPage} sur {totalPages} •{" "}
                  {filteredPurchases.length} résultat
                  {filteredPurchases.length !== 1 ? "s" : ""}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal des services - Version améliorée */}
      <AnimatePresence>
        {showServicesModal && selectedPurchase && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={handleCloseModal}
            />

            {/* Modal Container */}
            <div className="flex min-h-screen items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-white w-full max-w-3xl max-h-[90vh] flex flex-col"
              >
                {/* Header fixe */}
                <div className="bg-gradient-to-r from-primary to-pink-600 p-6 flex-shrink-0">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="font-poppins font-bold text-xl text-white">
                        Détails des Services
                      </h2>
                      <p className="font-nunito text-sm text-white/90 mt-1">
                        {formatDateTime(selectedPurchase.created_at)}
                      </p>
                    </div>
                    <button
                      onClick={handleCloseModal}
                      className="text-white/80 hover:text-white transition-colors p-2"
                    >
                      <BiX className="w-6 h-6" />
                    </button>
                  </div>

                  {/* Informations résumé */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <p className="font-nunito text-xs text-white/80">
                        Statut
                      </p>
                      <p
                        className={`inline-flex items-center gap-1 px-2 py-1 mt-1 ${getStatusColor(selectedPurchase.status)} text-xs font-semibold`}
                      >
                        {getStatusIcon(selectedPurchase.status)}
                        {getStatusLabel(selectedPurchase.status)}
                      </p>
                    </div>
                    <div>
                      <p className="font-nunito text-xs text-white/80">Type</p>
                      <p className="font-poppins font-semibold text-sm text-white">
                        {getTypeLabel(selectedPurchase)}
                      </p>
                    </div>
                    <div>
                      <p className="font-nunito text-xs text-white/80">
                        Montant
                      </p>
                      <p className="font-poppins font-bold text-base text-white">
                        {formatAmount(selectedPurchase.amount)} FCFA
                      </p>
                    </div>
                    <div>
                      <p className="font-nunito text-xs text-white/80">
                        Méthode
                      </p>
                      <p
                        className={`inline-flex items-center gap-1 px-2 py-1 mt-1 ${getMethodColor(selectedPurchase.method)} text-xs font-semibold`}
                      >
                        {getMethodIcon(selectedPurchase.method)}
                        {selectedPurchase.method}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Contenu scrollable */}
                <div className="flex-1 overflow-y-auto p-6">
                  {/* Informations de paiement (extraData) */}
                  <div className="mb-6">
                    <h3 className="font-poppins font-semibold text-lg text-gray-900 mb-3">
                      Informations de paiement
                    </h3>
                    <div className="bg-gradient-to-r from-blue-50 to-white p-4 border border-blue-200">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-blue-100">
                          <BiUser className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-poppins font-semibold text-base text-gray-900">
                            {selectedPurchase.extraData?.name ||
                              `${selectedPurchase.user.nom} ${selectedPurchase.user.prenom}`}
                          </p>
                          <p className="font-nunito text-sm text-gray-600 mt-1">
                            {selectedPurchase.extraData?.email ||
                              selectedPurchase.user.email}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 font-nunito text-sm text-gray-700">
                        <div>
                          <span className="text-gray-500 font-medium">
                            Téléphone:
                          </span>{" "}
                          {selectedPurchase.extraData?.telephone ||
                            selectedPurchase.user.telephone}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* {selectedPurchase.extraData && (
                  )} */}

                  {/* Liste des services */}
                  <div>
                    <h3 className="font-poppins font-semibold text-lg text-gray-900 mb-4">
                      Services ({getPurchaseServices(selectedPurchase).length})
                    </h3>

                    {getPurchaseServices(selectedPurchase).length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 border border-gray-200">
                        <BiListUl className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="font-nunito text-base text-gray-600">
                          Aucun service trouvé
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-3 mb-6">
                          {getPurchaseServices(selectedPurchase).map(
                            (service, index) => (
                              <div
                                key={index}
                                className="border border-gray-200 p-4 hover:bg-gray-50 transition-colors"
                              >
                                <div className="flex justify-between items-start mb-3">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className="font-poppins font-semibold text-base text-gray-900">
                                        {service.service?.nom ||
                                          `Service ${index + 1}`}
                                      </span>
                                      {service.service?.id && (
                                        <span className="font-nunito text-xs text-gray-500 bg-gray-100 px-2 py-0.5">
                                          #{service.service.id}
                                        </span>
                                      )}
                                    </div>
                                    {service.service?.description && (
                                      <p className="font-nunito text-sm text-gray-600">
                                        {service.service.description}
                                      </p>
                                    )}
                                  </div>
                                  <div className="text-right">
                                    <div className="font-poppins font-bold text-lg text-gray-900">
                                      {formatAmount(
                                        service.unitPrice || service.price || 0,
                                      )}{" "}
                                      FCFA
                                    </div>
                                    <div className="font-nunito text-xs text-gray-500">
                                      unitaire
                                    </div>
                                  </div>
                                </div>

                                <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                                  <div className="font-nunito text-sm text-gray-700">
                                    <span className="font-semibold">
                                      Quantité:
                                    </span>{" "}
                                    {service.quantity || 1}
                                  </div>
                                  <div className="font-poppins font-bold text-base text-gray-900">
                                    Total:{" "}
                                    {formatAmount(
                                      parseFloat(
                                        service.unitPrice || service.price || 0,
                                      ) * (service.quantity || 1),
                                    )}{" "}
                                    FCFA
                                  </div>
                                </div>
                              </div>
                            ),
                          )}
                        </div>

                        {/* Total des services */}
                        <div className="bg-gradient-to-r from-primary/5 to-pink-500/5 border border-primary/20 p-4">
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-poppins font-semibold text-base text-gray-900">
                                Total des services
                              </p>
                              <p className="font-nunito text-sm text-gray-600">
                                {getPurchaseServices(selectedPurchase).length}{" "}
                                service
                                {getPurchaseServices(selectedPurchase)
                                  .length !== 1
                                  ? "s"
                                  : ""}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-poppins font-bold text-2xl text-gray-900">
                                {formatAmount(
                                  calculateServicesTotal(
                                    getPurchaseServices(selectedPurchase),
                                  ),
                                )}{" "}
                                FCFA
                              </p>
                              <p className="font-nunito text-sm text-gray-600">
                                Montant total des services
                              </p>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Footer fixe */}
                <div className="border-t border-gray-200 p-6 bg-white flex-shrink-0">
                  <div className="flex justify-end">
                    <button
                      onClick={handleCloseModal}
                      className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 font-nunito font-semibold text-sm transition-colors"
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
